import type { DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import type { SelectionContext } from '@atlaskit/editor-common/types';
import type { JSONDocNode, JSONNode } from '@atlaskit/editor-json-transformer/types';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type {
	PageContentCommandResult,
	ReadContentCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';

import { log } from '../utils/aifc-logging';

import { CommandHandler } from './command-handler';
import type { PassThroughCommandProps } from './command-handler';

const REMIX_UI_PATH_REGEX = new RegExp('remix\\/ui\\/([^/?#]+)', 'u');

const isMediaNode = (node?: JSONNode) => {
	if (!node) return false;
	if (node.type === 'media') {
		return typeof (node.attrs as { id?: string })?.id === 'string';
	}
	if (node.type === 'mediaSingle' && Array.isArray(node.content) && node.content.length > 0) {
		const first = node.content[0] as JSONNode;
		return first?.type === 'media' && typeof (first.attrs as { id?: string })?.id === 'string';
	}
	return false;
};

const isMediaNodeSelected = (selection?: SelectionContext) => {
	return selection?.localIds?.length === 1 && isMediaNode(selection?.selectionFragment?.at(0));
};

/**
 * Extracts the media file ID from the selected ADF fragment when the selection is a media node.
 */
const extractMediaFileId = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}
	const first = selectionFragment[0];
	if (!first) return undefined;
	if (first.type === 'media') {
		return (first.attrs as { id?: string })?.id;
	}
	if (first.type === 'mediaSingle' && Array.isArray(first.content) && first.content[0]) {
		const media = first.content[0] as JSONNode;
		return media?.type === 'media' ? (media.attrs as { id?: string })?.id : undefined;
	}
	return undefined;
};

/**
 * Extracts the media collection id from the selected ADF fragment when the selection is a media node.
 * Sent to the backend so it can resolve the current media's collection when editing an existing image.
 */
const extractMediaCollection = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}
	const first = selectionFragment[0];
	if (!first) return undefined;
	if (first.type === 'media') {
		return (first.attrs as { collection?: string })?.collection;
	}
	if (first.type === 'mediaSingle' && Array.isArray(first.content) && first.content[0]) {
		const media = first.content[0] as JSONNode;
		return media?.type === 'media'
			? (media.attrs as { collection?: string })?.collection
			: undefined;
	}
	return undefined;
};

const isMauiAppNode = (node?: JSONNode) => {
	return (
		node?.type === 'extension' &&
		(node.attrs as { extensionKey?: string })?.extensionKey === 'native-embed:maui'
	);
};

const isMauiAppNodeSelected = (selection?: SelectionContext) => {
	return selection?.localIds?.length === 1 && isMauiAppNode(selection?.selectionFragment?.at(0));
};

const isADFEntity = (value: ReadContentCommandProperties['prompt']): value is ADFEntity =>
	typeof value === 'object' &&
	value !== null &&
	!Array.isArray(value) &&
	typeof (value as { type?: unknown }).type === 'string';

// Safe here means "does not introduce external/contextual references that the
// editor-minion fast path cannot resolve." Formatting marks are allowed; link
// marks are checked separately because they can carry external context.
const SAFE_PROMPT_NODE_TYPES = new Set([
	'doc',
	'paragraph',
	'text',
	'emoji',
	'hardBreak',
	'date',
	'status',
	'placeholder',
]);

// Unknown nodes are treated as rich by omission from SAFE_PROMPT_NODE_TYPES.
const isSafePromptNode = (node: ADFEntity): boolean =>
	SAFE_PROMPT_NODE_TYPES.has(node.type) && !node.marks?.some((mark) => mark.type === 'link');

const hasRichPromptContent = (prompt: ADFEntity): boolean => {
	let hasRichContent = false;
	traverse(prompt, {
		any: (node) => {
			if (!isSafePromptNode(node)) {
				hasRichContent = true;
			}
		},
	});

	return hasRichContent;
};

const isPlainPrompt = (prompt?: ReadContentCommandProperties['prompt']): boolean =>
	isADFEntity(prompt) && prompt.type === 'doc' && !hasRichPromptContent(prompt);

/**
 * Extracts the Maui content ID from the selected ADF fragment.
 */
const extractMauiContentId = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}

	const mauiApp = selectionFragment.find((node) => isMauiAppNode(node));
	const attrs = mauiApp?.attrs as
		| { extensionKey?: string; parameters?: { macroParams?: { url?: { value?: string } } } }
		| undefined;

	const url: string | undefined = attrs?.parameters?.macroParams?.url?.value;
	if (url) {
		// We only want the ID, which is the part after /remix/ui/
		const match = REMIX_UI_PATH_REGEX.exec(url);
		return match?.[1];
	}
	return undefined;
};

export const buildContentContext = async ({
	props,
	doc,
	selectionFragment,
	selectionLocalIds,
	selectionMarkdown,
	contentMauiId,
	dynamicUiSource,
	dynamicUiType,
	dynamicUiSubtype,
	mediaFileId,
	mediaCollection,
	startIndex,
	endIndex,
	isEditorInlineRovoActive,
	aiExperienceProduct,
	source,
	prompt,
}: {
	aiExperienceProduct?: string;
	contentMauiId?: string;
	doc: DocNode | JSONDocNode | undefined;
	dynamicUiSource?: PageContentCommandResult['dynamicUiSource'];
	dynamicUiSubtype?: string;
	dynamicUiType?: string;
	endIndex?: number;
	isEditorInlineRovoActive?: boolean;
	mediaCollection?: string;
	mediaFileId?: string;
	prompt?: ReadContentCommandProperties['prompt'];
	props: PassThroughCommandProps;
	selectionFragment: string;
	selectionLocalIds: string;
	selectionMarkdown: string;
	source?: string;
	startIndex?: number;
}): Promise<PageContentCommandResult> => {
	let additionalContextFromProps = props.additionalContext || { isViewMode: false };
	if (typeof additionalContextFromProps === 'function') {
		try {
			additionalContextFromProps = await additionalContextFromProps();
			// eslint-disable-next-line no-unused-vars
		} catch (err) {
			additionalContextFromProps = {};
		}
	}
	const {
		isViewMode = false,
		isDraftLockedForEditing = false,
		...additionalContext
	} = additionalContextFromProps || {};

	// Backend document updates are delivered as NCS steps, so they can only reach
	// editors backed by a collab document.
	const hasRealTimeCollab = !!props.editorAPI?.collabEdit;

	// HTML+ v2 incremental tool-call streaming capability advertisement.
	// Per FE spec §2.5 + §3.8, FE sends a single int `editorStreamingContractVersion`
	// on the editor JSON block. Backend reads `>= 2` to opt the request into
	// the v2 wire format. Gated by `platform_editor_use_html_plus_streaming_parser`
	// — when the gate is off the field is omitted entirely so v1 wire bytes
	// stay byte-identical to today.
	const editorStreamingContractVersion = expValEquals(
		'platform_editor_use_html_plus_streaming_parser',
		'isEnabled',
		true,
	)
		? 2
		: undefined;

	// When true, the backend will skip the orchestrator and call the editor minion directly.
	const useFastPath =
		isEditorInlineRovoActive &&
		aiExperienceProduct === 'CONFLUENCE' &&
		source === 'CONFLUENCE_INLINE_EDITOR' &&
		isPlainPrompt(prompt) &&
		isExperimentEnabled('platform_editor_ai_minion_skip_orchestrator')
			? true
			: undefined;

	return {
		contentId: props.contentId,
		contentType: props.contentType,
		contentMauiId,
		dynamicUiSource,
		dynamicUiType,
		dynamicUiSubtype,
		mediaFileId,
		mediaCollection,
		pageBody: doc,
		selectionFragment,
		selectionLocalIds,
		selectionMarkdown,
		startIndex,
		endIndex,
		isViewMode,
		isDraftLockedForEditing,
		hasRealTimeCollab,
		useGenericEditorSkill: props.useGenericEditorSkill,
		additionalContext: Object.keys(additionalContext).length > 0 ? additionalContext : undefined,
		...(editorStreamingContractVersion !== undefined ? { editorStreamingContractVersion } : {}),
		...(useFastPath !== undefined ? { useFastPath } : {}),
	};
};

export class ReadContentCommandHandler extends CommandHandler<
	ReadContentCommandProperties,
	PageContentCommandResult | null
> {
	async execute(payload?: ReadContentCommandProperties): Promise<PageContentCommandResult | null> {
		const defaultResult: PageContentCommandResult = await buildContentContext({
			props: this.props,
			doc: undefined,
			selectionFragment: '',
			selectionLocalIds: '',
			selectionMarkdown: '',
		});

		if (
			expValEquals('cc-markdown-mode', 'isEnabled', true) &&
			this.props.editorAPI?.markdownMode?.sharedState.currentState()?.isMarkdownMode
		) {
			return null;
		}

		try {
			if (isExperimentEnabled('platform_editor_local_id_reliability')) {
				this.props.editorAPI.core.actions.execute(
					this.props.editorAPI.localId?.commands.addMissingLocalIds({
						onRepaired: ({ repairedNodeCount }) => {
							this.props.editorAPI.analytics?.actions.fireAnalyticsEvent({
								action: ACTION.LOCAL_IDS_REPAIRED,
								actionSubject: ACTION_SUBJECT.AI_STREAMING,
								actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
								attributes: { repairedNodeCount },
								eventType: EVENT_TYPE.OPERATIONAL,
							});
						},
					}),
				);
			}

			const doc = fg('platform_editor_aifc_rovo_bridge_partial_doc')
				? this.props.editorAPI?.aiExperience?.actions.getPartialDocumentFromSelection()
				: await this.props.editorAPI.core.actions.requestDocumentAsync();
			if (!doc) {
				// TODO: EDITOR-123 - report this error
				return defaultResult;
			}

			const aiExperiencePluginState =
				this.props.editorAPI.aiExperience?.sharedState?.currentState()
					?.aiDecorationExperiencePluginState;
			const isEditorInlineRovoActive = aiExperiencePluginState?.isRunning;
			const aiExperienceProduct = aiExperiencePluginState?.product;
			const selection = this.props.editorAPI.aiExperience?.actions.getAISelectionContext();
			const selectionFragment = selection?.selectionFragment
				? JSON.stringify(selection.selectionFragment)
				: '';
			const selectedMediaFileId = isMediaNodeSelected(selection)
				? extractMediaFileId(selection?.selectionFragment)
				: undefined;
			const selectedMediaDynamicUiSubtype =
				selectedMediaFileId && isExperimentEnabled('cc_maui_polish_changes_batch_5')
					? this.props.editorAPI.malleableUi?.actions.getRemixDynamicUiSubtype(selectedMediaFileId)
					: undefined;
			const content: PageContentCommandResult = await buildContentContext({
				props: this.props,
				doc,
				selectionFragment,
				startIndex: selection?.startIndex ?? undefined,
				endIndex: selection?.endIndex ?? undefined,
				selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
				selectionMarkdown: selection?.selectionMarkdown ?? '',
				isEditorInlineRovoActive,
				aiExperienceProduct,
				source: payload?.source,
				prompt: payload?.prompt,
				...(expValEquals('cc-maui-experiment', 'isEnabled', true)
					? {
							...(isMauiAppNodeSelected(selection)
								? { contentMauiId: extractMauiContentId(selection?.selectionFragment) }
								: {}),
							...(selectedMediaFileId
								? {
										mediaFileId: selectedMediaFileId,
										...(fg('platform_editor_maui_remix_media_collection')
											? { mediaCollection: extractMediaCollection(selection?.selectionFragment) }
											: {}),
										dynamicUiType: 'infographic',
										...(selectedMediaDynamicUiSubtype
											? { dynamicUiSubtype: selectedMediaDynamicUiSubtype }
											: {}),
									}
								: {}),
							dynamicUiSource: {
								contentType: 'page',
								startLocalId: selection?.localIds?.[0],
								endLocalId: selection?.localIds?.[selection.localIds.length - 1],
								startIndex: selection?.startIndex ?? undefined,
								endIndex: selection?.endIndex ?? undefined,
								fragmentAdf: selectionFragment,
							},
						}
					: {}),
			});

			log('handle read-content-command result', {
				additionalContext: this.props.additionalContext,
				doc,
				selection,
				content,
			});

			return content ?? defaultResult;
		} catch {
			// TODO: EDITOR-123 - report this error
			return defaultResult;
		}
	}
}
